"use client";

import React, { useState, useEffect } from "react";
import { CentralizedDrawer, Button, LoadingSpinner } from "@wtc/ui";
import { Download, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import type {
  GoogleDriveFile,
  GoogleDrivePreviewDrawerProps,
} from "./GoogleDrivePreviewDrawer/types";
import { canPreviewFile, getFileTypeIcon, getFileTypeLabel } from "./GoogleDrivePreviewDrawer/utils";
import { useDriveFileDownload } from "./GoogleDrivePreviewDrawer/useDriveFileDownload";
import { PreviewContent } from "./GoogleDrivePreviewDrawer/PreviewContent";
import { UnavailablePreview } from "./GoogleDrivePreviewDrawer/UnavailablePreview";

export type { GoogleDriveFile, GoogleDrivePreviewDrawerProps };

export function GoogleDrivePreviewDrawer({
  file,
  isSecondAccount = false,
  className = "",
}: GoogleDrivePreviewDrawerProps) {
  const [previewError, setPreviewError] = useState<string | null>(null);
  const { isDownloading, download } = useDriveFileDownload(file, isSecondAccount);

  // Reset error when file changes
  useEffect(() => {
    setPreviewError(null);
  }, [file?.id]);

  const handleOpenInDrive = () => {
    if (file?.webViewLink) {
      window.open(file.webViewLink, "_blank");
    } else {
      toast.error("No view link available for this file");
    }
  };

  const handlePreviewError = () => {
    setPreviewError(
      "Unable to load preview. The file may be too large or in an unsupported format."
    );
  };

  if (!file) {
    return null;
  }

  const canPreview = canPreviewFile(file);

  return (
    <CentralizedDrawer
      drawerId="mobile-google-drive-preview"
      side="bottom"
      height="95vh"
      className={className}
      title={file.name}
      description={getFileTypeLabel(file.mimeType)}
      showCloseButton={true}
      enableSwipeGesture={true}
    >
      <div className="flex-1 overflow-y-auto p-4">
        {canPreview ? (
          <div className="space-y-4">
            <PreviewContent
              file={file}
              previewError={previewError}
              canDownload={file.canDownload}
              isDownloading={isDownloading}
              onPreviewError={handlePreviewError}
              onOpenInDrive={handleOpenInDrive}
              onDownload={download}
            />
          </div>
        ) : (
          <UnavailablePreview
            title="Preview Not Available"
            description="This file type cannot be previewed. You can download it or open it in Google Drive to view the content."
            fileTypeIcon={getFileTypeIcon(file.mimeType)}
            canDownload={file.canDownload}
            isDownloading={isDownloading}
            onOpenInDrive={handleOpenInDrive}
            onDownload={download}
          />
        )}
        <div className="flex items-center gap-2 mt-4">
          {file.canDownload && (
            <Button onClick={download} disabled={isDownloading} variant="outline" size="sm">
              {isDownloading ? (
                <LoadingSpinner className="h-4 w-4 mr-2" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              Download
            </Button>
          )}

          <Button onClick={handleOpenInDrive} variant="outline" size="sm">
            <ExternalLink className="h-4 w-4 mr-2" />
            Open in Drive
          </Button>
        </div>
      </div>
    </CentralizedDrawer>
  );
}
